import React from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './BusinessNav.styles';
import { Button } from '@/components/UI/Button/Button';
import { useTheme } from '@/hooks/theme/useTheme';
import { ChangeLang } from '@/components/ChangeLang/ChangeLang';
import { Overlay } from '@/components/UI/Overlay/Overlay.styles';
import { makeHrefForPhone } from '@/utils/helpers';
import { useBodyOverflow } from '@/hooks/useBodyOverflow';
import { CloseBusinessMenuIcon, MenuIcon } from '@/components/UI/Icons/Icons';
import { ROUTES } from '@/constants/routes';
import { Portal } from '@/components/UI/Portal/Portal';
import NoSsr from '@/components/NoSsr/NoSsr';
import { CONTACT_US } from '@/constants/contactUs';
import { CustomLink } from '@/components/CustomLink/CustomLink';
import { useBridgeState } from '@/bridge/hooks';
import { Languages } from '@/constants/common';

interface BusinessNavProps {
  isOpen: boolean;
  toggleNav: () => void;
}

export const BusinessNav: React.FC<BusinessNavProps> = ({
  isOpen,
  toggleNav,
}) => {
  const { t, i18n } = useTranslation();
  const { isDarkTheme } = useTheme();
  const { isWebView } = useBridgeState();

  const advertisingHref = ROUTES.advertising_landing(
    i18n.language === Languages.Az ? '' : `${Languages.Ru}.`
  );

  useBodyOverflow(isOpen);

  return (
    <>
      <Button
        data-testid="business-nav-button"
        appearance="transparent"
        hasPadding={false}
        onClick={toggleNav}
      >
        {isOpen ? <CloseBusinessMenuIcon /> : <MenuIcon />}
      </Button>
      <NoSsr>
        <Portal>
          <Overlay
            data-testid="business-nav-overlay"
            $isOpen={isOpen}
            onClick={toggleNav}
          />

          <S.BusinessNavigation
            data-testid="business-nav-drawer"
            $isOpen={isOpen}
          >
            <S.BusinessNavContainer>
              <S.BusinessNavColumn>
                <S.BusinessNavItem
                  href={ROUTES.shops}
                  as={CustomLink}
                  target="_blank"
                >
                  {t('catalogue.stores')}
                </S.BusinessNavItem>
                <S.BusinessNavItem
                  href={ROUTES.faq}
                  as={CustomLink}
                  target="_blank"
                >
                  {t('common.help')}
                </S.BusinessNavItem>
              </S.BusinessNavColumn>

              <S.BusinessNavColumn>
                <S.BusinessNavItem
                  href={ROUTES.about}
                  as={CustomLink}
                  target="_blank"
                >
                  {t('common.about_project')}
                </S.BusinessNavItem>
                <S.BusinessNavItem
                  href={ROUTES.terms}
                  as={CustomLink}
                  target="_blank"
                >
                  {t('common.terms_of_use')}
                </S.BusinessNavItem>
                <S.BusinessNavItem
                  href={ROUTES.rules}
                  as={CustomLink}
                  target="_blank"
                >
                  {t('common.rules')}
                </S.BusinessNavItem>
                <S.BusinessNavItem
                  href={advertisingHref}
                  as={CustomLink}
                  target="_blank"
                  data-stat="advertising-business-burger-btn"
                >
                  {t('common.advertising_on_site')}
                </S.BusinessNavItem>
              </S.BusinessNavColumn>

              <S.BusinessNavColumn $isLeftContent>
                <S.BusinessNavItem $isLast $isHead>
                  {t('common.connect_with_us')}
                </S.BusinessNavItem>

                <S.BusinessNavItem
                  $isLast
                  as="a"
                  href={makeHrefForPhone(CONTACT_US.PHONE)}
                  $leftIcon={{
                    name: `phone${isDarkTheme ? '-light' : ''}.svg`,
                    width: 18,
                    height: 18,
                  }}
                >
                  {CONTACT_US.PHONE}
                </S.BusinessNavItem>

                <S.BusinessNavItem
                  $isContactUsItem
                  as="a"
                  href={`mailto:${CONTACT_US.EMAIL}`}
                  target="_blank"
                >
                  {CONTACT_US.EMAIL}
                </S.BusinessNavItem>

                {!isWebView && (
                  <S.BusinessNavItem $leftIcon={{ name: 'language.svg' }}>
                    <ChangeLang />
                  </S.BusinessNavItem>
                )}

                <S.BusinessNavSocials>
                  <S.BusinessNavItem
                    $isLast
                    as="a"
                    href={CONTACT_US.FACEBOOK}
                    target="_blank"
                    $leftIcon={{
                      name: 'fb_grey.svg',
                      width: 20,
                      height: 20,
                    }}
                  />

                  <S.BusinessNavItem
                    $isLast
                    as="a"
                    href={CONTACT_US.INSTAGRAM}
                    target="_blank"
                    $leftIcon={{
                      name: 'instagram-alt.svg',
                      width: 20,
                      height: 20,
                    }}
                  />
                </S.BusinessNavSocials>
              </S.BusinessNavColumn>
            </S.BusinessNavContainer>
          </S.BusinessNavigation>
        </Portal>
      </NoSsr>
    </>
  );
};
